import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSupabaseClient } from './use-supabase';

/**
 * Per-user starred pages, scoped to (user_id, org_id).
 *
 * Backed by the `user_favorites` table created in the IA1 foundation
 * migration. Star/unstar mutations are immediate (no optimistic UI —
 * the table is small, the round-trip is fast, and the user expects
 * the truth-of-the-DB to be the source of state).
 *
 * Three exports:
 *   - useFavorites(orgId)     — list current user's favorites
 *   - useIsFavorite(orgId, route)
 *   - useToggleFavorite(orgId) — star/unstar from anywhere
 */

export interface FavoriteRow {
  id: string;
  route: string;
  module_slug: string | null;
  workspace_slug: string | null;
  page_title: string;
  starred_at: string;
}

export interface ToggleFavoriteInput {
  route: string;
  page_title: string;
  module_slug?: string | null;
  workspace_slug?: string | null;
}

function favoritesKey(userId: string | null | undefined, orgId: string | null) {
  return ['user_favorites', userId ?? null, orgId] as const;
}

export function useFavorites(orgId: string | null) {
  const supabase = useSupabaseClient();
  // user_id in the query key so signing out + signing in (or switching
  // users in a shared browser tab) doesn't serve user A's cached
  // favorites to user B until staleTime elapses. Same defence the
  // org dimension already provides for tenant switches.
  const { data: userData } = useQuery({
    queryKey: ['user_favorites', 'current-user-id'],
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await supabase.auth.getUser();
      return data.user?.id ?? null;
    },
  });
  const userId = userData ?? null;

  return useQuery({
    queryKey: favoritesKey(userId, orgId),
    enabled: !!orgId && !!userId,
    staleTime: 30_000,
    queryFn: async (): Promise<FavoriteRow[]> => {
      // Explicit org_id filter — never rely on RLS alone for tenant
      // scoping. RLS narrows to (user_id, current_org()), but if the
      // backend JWT claim hasn't caught up to the user's current org
      // selection, RLS could surface a different org's favorites. The
      // explicit filter belt-and-suspenders the contract.
      const { data, error } = await supabase
        .from('user_favorites')
        .select('id, route, module_slug, workspace_slug, page_title, starred_at')
        .eq('org_id', orgId as string)
        .order('starred_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as FavoriteRow[];
    },
  });
}

export function useIsFavorite(orgId: string | null, route: string): boolean {
  const { data = [] } = useFavorites(orgId);
  return data.some((r) => r.route === route);
}

export function useToggleFavorite(orgId: string | null) {
  const supabase = useSupabaseClient();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: ToggleFavoriteInput) => {
      if (!orgId) throw new Error('No active org');
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (!userId) throw new Error('Not authenticated');

      // Existence check scoped by both route AND org so a star in
      // org A doesn't shadow a star in org B for the same URL.
      const existingRes = await supabase
        .from('user_favorites')
        .select('id')
        .eq('route', input.route)
        .eq('org_id', orgId);
      if (existingRes.error) throw existingRes.error;
      const existing = existingRes.data?.[0];

      // Surface DB write errors instead of swallowing — otherwise the
      // UI shows a successful toggle while the row didn't persist.
      if (existing) {
        const { error } = await supabase.from('user_favorites').delete().eq('id', existing.id);
        if (error) throw error;
        return { starred: false as const, route: input.route };
      } else {
        const { error } = await supabase.from('user_favorites').insert({
          user_id: userId,
          org_id: orgId,
          route: input.route,
          module_slug: input.module_slug ?? null,
          workspace_slug: input.workspace_slug ?? null,
          page_title: input.page_title,
        });
        if (error) throw error;
        return { starred: true as const, route: input.route };
      }
    },
    onSuccess: () => {
      // Invalidate by prefix so any user_favorites query for this
      // org refreshes, regardless of which user-keyed instance is
      // mounted. Simpler than threading userId through the mutation.
      void qc.invalidateQueries({ queryKey: ['user_favorites'] });
    },
  });
}
